import React from 'react';
import { Loader2, Wand2, X, Plus, RefreshCw } from 'lucide-react';

export const AiPreviewModal = ({ aiPreview, aiGenerating, aiTone, setAiTone, aiSteps, setAiSteps, aiAbTesting, setAiAbTesting, onRegenerate, onSaveAsTemplate, onClose }) => {
  if (!aiPreview) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="ai-preview-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 bg-gradient-to-br from-[#0A1F44] to-[#1a3a6b] rounded-lg flex items-center justify-center">
              <Wand2 className="h-4 w-4 text-[#D4AF37]" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-[#0A1F44]">{aiPreview.template_name}</h2>
              <p className="text-[10px] text-gray-500">{aiPreview.description}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X className="h-4 w-4 text-gray-400" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="flex flex-wrap gap-3 mb-2">
            <span className="px-2 py-0.5 bg-[#D4AF37]/20 text-[#0A1F44] text-[10px] font-medium rounded">AI-Generated</span>
            {aiPreview.ab_testing && <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-medium rounded">A/B Testing</span>}
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded">{aiPreview.steps_count} steps</span>
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded">{aiPreview.total_days} days</span>
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded">{aiPreview.company_context?.company}</span>
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded">Score: {aiPreview.company_context?.score}</span>
          </div>
          {aiPreview.sequences?.[0]?.steps?.map((step, si) => {
            const variants = step.variants || [];
            const hasAB = variants.length > 1;
            return (
              <div key={si} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`ai-step-${si}`}>
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-50">
                  <div className="h-5 w-5 bg-[#0A1F44] rounded-full flex items-center justify-center">
                    <span className="text-[9px] font-bold text-[#D4AF37]">{si + 1}</span>
                  </div>
                  <span className="text-xs font-medium text-[#0A1F44]">Step {si + 1}</span>
                  <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 text-[9px] rounded">{step.delay} {step.delay_unit} delay</span>
                  {hasAB && <span className="px-1.5 py-0.5 bg-purple-50 text-purple-600 text-[9px] font-medium rounded">A/B Split</span>}
                </div>
                {hasAB ? (
                  <div className="grid grid-cols-2 divide-x divide-gray-200">
                    {variants.map((v, vi) => (
                      <div key={vi} className={`p-4 ${vi === 0 ? 'bg-white' : 'bg-gray-50/50'}`} data-testid={`ai-step-${si}-variant-${vi}`}>
                        <div className="flex items-center gap-1.5 mb-2">
                          <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded ${vi === 0 ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                            Variant {vi === 0 ? 'A' : 'B'}
                          </span>
                          <span className="text-[9px] text-gray-400">{vi === 0 ? 'Question/Curiosity' : 'Direct/Data-driven'}</span>
                        </div>
                        <p className="text-[10px] text-gray-500 mb-0.5">Subject:</p>
                        <p className="text-xs font-medium text-[#0A1F44] mb-2">{v.subject}</p>
                        <p className="text-[10px] text-gray-500 mb-0.5">Body:</p>
                        <div className="bg-gray-50 p-2.5 rounded-lg text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">{v.body}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4">
                    <p className="text-xs text-gray-500 mb-1">Subject:</p>
                    <p className="text-sm font-medium text-[#0A1F44] mb-3">{variants[0]?.subject}</p>
                    <p className="text-xs text-gray-500 mb-1">Body:</p>
                    <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{variants[0]?.body}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50 rounded-b-xl">
          <div className="flex items-center gap-2">
            <select value={aiTone} onChange={e => setAiTone(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none"
              data-testid="ai-tone-select">
              <option value="professional">Professional</option>
              <option value="casual">Casual</option>
              <option value="direct">Direct</option>
            </select>
            <select value={aiSteps} onChange={e => setAiSteps(parseInt(e.target.value))}
              className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none"
              data-testid="ai-steps-select">
              <option value={2}>2 steps</option>
              <option value={3}>3 steps</option>
              <option value={4}>4 steps</option>
              <option value={5}>5 steps</option>
            </select>
            <label className="flex items-center gap-1.5 px-2 py-1 rounded border border-gray-300 cursor-pointer hover:bg-gray-100 transition-colors" data-testid="ai-ab-toggle">
              <input type="checkbox" checked={aiAbTesting} onChange={e => setAiAbTesting(e.target.checked)}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 h-3 w-3" />
              <span className="text-xs font-medium text-gray-700">A/B Test</span>
            </label>
            <button onClick={() => onRegenerate(aiPreview.company_name)}
              disabled={!!aiGenerating}
              className="px-3 py-1.5 text-xs font-medium text-[#0A1F44] border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 flex items-center gap-1"
              data-testid="ai-regenerate-btn">
              {aiGenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Regenerate
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose}
              className="px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100">
              Close
            </button>
            <button onClick={onSaveAsTemplate}
              className="px-4 py-1.5 bg-[#D4AF37] text-[#0A1F44] text-xs font-bold rounded-lg hover:bg-[#c5a030] flex items-center gap-1"
              data-testid="ai-save-template-btn">
              <Plus className="h-3 w-3" /> Save as Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
